import { useEffect, useState } from 'react';
import { useHub, usePrivate } from './context';
import { loadFriends, loadLegend, loadRelationships, REL_DAYS, type RelationshipData } from './activity';
import { Card, Counting } from './ui';

/** Data and pieces several hub tabs share (Activity, Clan, Legend). */

// who the player plays with: one analysis shared by Heat, Hours and Clan > Relationships, with its progress
const relJobs = new Map<string, { promise: Promise<RelationshipData>; progress: [number, number]; listeners: Set<() => void> }>();

export function useRelationships(days: number = REL_DAYS) {
  const { player, stamp } = useHub();
  const [, tick] = useState(0);
  const [data, setData] = useState<RelationshipData>();
  const [error, setError] = useState<string>();
  const [turn, setTurn] = useState(0);
  const key = player ? `${player.membershipId}|${days}|${turn}` : '';
  useEffect(() => {
    if (!player) return;
    let job = relJobs.get(key);
    if (!job) {
      const j = { progress: [0, 0] as [number, number], listeners: new Set<() => void>(), promise: null as unknown as Promise<RelationshipData> };
      j.promise = loadRelationships(player, days, (d, t) => { j.progress = [d, t]; if (d % 5 === 0 || d === t) j.listeners.forEach((l) => l()); });
      j.promise.catch(() => relJobs.delete(key));
      relJobs.set(key, j);
      job = j;
    }
    let live = true;
    const l = () => live && tick((x) => x + 1);
    job.listeners.add(l);
    setData(undefined); setError(undefined);
    job.promise.then((d) => live && setData(d)).catch((e) => live && setError(`Your activities could not be analysed (${e instanceof Error ? e.message : e}).`));
    return () => { live = false; job!.listeners.delete(l); };
  }, [key, stamp]);   // eslint-disable-line react-hooks/exhaustive-deps
  const progress = relJobs.get(key)?.progress ?? [0, 0];
  return { data, error, progress, again: () => { relJobs.delete(key); setTurn((t) => t + 1); } };
}

export function useFriends() {
  return usePrivate('friends', (t) => loadFriends(t));
}

export function useLegend() {
  const h = useHub();
  return usePrivate('legend', () => loadLegend(h.player!), { public: true });
}

/** A label in small capitals over a big number. */
export function StatTile({ label, value, color = '#ffffff', sub, wide, title }: { label: string; value: number | string; color?: string; sub?: string; wide?: boolean; title?: string }) {
  return (
    <Card className={`g-stattile ${wide ? 'wide' : ''}`}>
      <div title={title}>
        <div className="g-stattile-label">{label.toUpperCase()}</div>
        <div className="g-stattile-value" style={{ color }}>{typeof value === 'number' ? <Counting value={value} /> : value}</div>
        {sub && <div className="g-dim small">{sub}</div>}
      </div>
    </Card>
  );
}

/** Hours to a whole number, as the app writes them. */
export const hrs = (seconds: number) => `${Math.floor(seconds / 3600).toLocaleString()} h`;
